import { useCallback } from 'react';
import { FieldValues, Path, useWatch, UseFormReturn } from 'react-hook-form';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat'; 
import { TimeRangePicker } from '@mui/x-date-pickers-pro/TimeRangePicker';
import { FormControl, FormHelperText } from '@mui/material';

dayjs.extend(customParseFormat);

interface Props<T extends FieldValues> {
  startName: Path<T>;
  endName: Path<T>;
  label?: string;
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  reactHookForm: UseFormReturn<T>;
}

export function FormTimeRangePickerInput<T extends FieldValues>({
  startName,
  endName,
  label,
  required,
  readOnly,
  disabled,
  reactHookForm,
}: Props<T>) {
  
  const { control, setValue, formState: { errors } } = reactHookForm;

  const startDateValue = useWatch({ control, name: startName });
  const endDateValue = useWatch({ control, name: endName });

  const startError = errors[startName]?.message as string | undefined;
  const endError = errors[endName]?.message as string | undefined;
  const hasError = !!startError || !!endError;
  const helperText = startError || endError || null;

  const getDayjsValue = (value: any): Dayjs | null => {
    if (!value) return null;
    
    let date = dayjs(value, 'HH:mm:ss'); 
    if (date.isValid()) return date;

    date = dayjs(value, 'HH:mm'); 
    if (date.isValid()) return date;
    
    return null;
  };

  const handleOnChange = useCallback(
    (newValue: [Dayjs | null, Dayjs | null]) => {
      const [start, end] = newValue;

      const formattedStart = (start && start.isValid()) ? start.format('HH:mm') : null;
      const formattedEnd = (end && end.isValid()) ? end.format('HH:mm') : null;

      setValue(startName, formattedStart as any, { shouldValidate: true, shouldDirty: true });
      setValue(endName, formattedEnd as any, { shouldValidate: true, shouldDirty: true });
    },
    [setValue, startName, endName]
  );

  return (
    <FormControl fullWidth error={hasError} sx={{ minWidth: 250 }}>
      <TimeRangePicker
        value={[getDayjsValue(startDateValue), getDayjsValue(endDateValue)]}
        onChange={handleOnChange}
        disabled={disabled}
        readOnly={readOnly}
        ampm={false}
        format="HH:mm" 
        slotProps={{
          textField: {
            size: 'small',
            label: label,
            required: required,
            error: hasError,
            fullWidth: true,
          },
          field: {
            clearable: true,
            onClear: () => {
                setValue(startName, null as any);
                setValue(endName, null as any);
            }
          }
        }}
      />
      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}